import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import MentoringNotesSection from '../components/MentoringNotesSection';
import { ChevronLeftIcon, InformationCircleIcon, UserCircleIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/authContext';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';

const MentoringNotesPage = () => {
  const { discipleId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [userAppID, setUserAppID] = useState('');
  const [discipleData, setDiscipleData] = useState(null);
  const [mentorDetails, setMentorDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInstructions, setShowInstructions] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser || !discipleId) {
        setLoading(false);
        console.warn("User not logged in or discipleId missing.");
        return;
      }
      setLoading(true);
      try {
        const userRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userRef);
        const appID = userDoc.exists() ? userDoc.data().appID : null;

        if (!appID) {
          console.error("User appID not found.");
          throw new Error("User appID not found.");
        }
        setUserAppID(appID);

        const [discipleDoc, mentorDetailsDoc] = await Promise.all([
          getDoc(doc(db, 'disciples', discipleId)),
          getDoc(doc(db, 'disciples', discipleId, 'mentorDetails', appID))
        ]);

        if (discipleDoc.exists()) {
          setDiscipleData(discipleDoc.data());
        } else {
           console.warn("Disciple document not found.");
        }
        if (mentorDetailsDoc.exists()) {
          setMentorDetails(mentorDetailsDoc.data());
        } else {
           console.warn("Mentor details document not found.");
        }

      } catch (error) {
        console.error('Error fetching data for MentoringNotesPage:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser, discipleId]);

  const handleToggleArchive = () => {
    setShowArchived(prev => !prev);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center space-x-5">
            <button
              onClick={() => navigate(`/disciple-menu/${discipleId}`)}
              className="text-gray-600 hover:text-gray-900 focus:outline-none"
              aria-label="Back to Disciple Menu"
            >
              <ChevronLeftIcon className="h-6 w-6" />
            </button>
            <div className="h-20 w-20 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden flex-shrink-0">
              {(mentorDetails?.photoURL || discipleData?.photoURL) ? (
                <img
                  src={mentorDetails?.photoURL || discipleData?.photoURL}
                  alt={discipleData?.name || 'Disciple'}
                  className="h-full w-full object-cover"
                />
              ) : (
                <UserCircleIcon className="h-16 w-16 text-gray-400" />
              )}
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl font-semibold text-gray-900 truncate">{discipleData?.name || 'Disciple'}</h1>
              <p className="text-gray-500 text-base truncate">Mentoring Notes</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        {loading ? (
          <p>Loading notes...</p> 
        ) : userAppID ? (
          <MentoringNotesSection
            discipleId={discipleId}
            userAppID={userAppID}
            showArchived={showArchived}
            onNoteAdded={() => {
              console.log("Note added on MentoringNotesPage");
            }}
          />
        ) : (
          <p className="text-red-500">Could not load user data. Please try again.</p>
        )}
      </div>
    </div>
  );
};

export default MentoringNotesPage; 